import { useContext, useEffect, useRef, useState } from "react"
import { faBusAlt } from "@fortawesome/pro-solid-svg-icons"
import { Autocomplete, FormControlLabel, Grid, Switch, TextField } from "@mui/material"
import { useFormikContext } from "formik"
import { useApi } from "auth/useApi3"
import { AppContext } from "components/AppContextProvider"
import SidebarInputLabel from "components/sidedrawer/SidebarInputLabel"
import { apiEndpoints } from "generated/apiEndpoints"
import { defaultSchema } from "./VehicleFormValidation"
import { Sidebar } from "components/sidedrawer/Sidebar2"
import { styled } from "@mui/material/styles"
import VehicleLoadingform from "./VehicleLoadingform"
import { useTranslation } from "react-i18next"

const StyledMaxWidthAutocomplete = styled(Autocomplete)({
  width: "100%"
})

const VehicleFormComponent = ({ editMode, mainPropellantResult, mainPropellantApi }) => {
  const { t } = useTranslation()
  const { values, setFieldValue, handleChange, handleBlur, touched, errors } = useFormikContext()
  const appContext = useContext(AppContext)
  const companies = appContext.appUser.customer.companies
  const departments = companies.flatMap((x) => x.departments)
  const showEnvironmentReportFields =
    appContext.appUser.customer.customerHasNonEditableReportsInVehicleRegistry

 useEffect(() => {
  if (appContext.isSidebarOpen != "VehicleList") return
  if (mainPropellantResult.status === 2) return
  mainPropellantApi.get(apiEndpoints.vehicleregistryadmin.getavailablemainpropellant)
  return () => {
    mainPropellantApi.leave()
  }
  }, [appContext.isSidebarOpen])

  return (
    <Grid container spacing={2}>
      <Grid xs={12} item>
        <SidebarInputLabel labelText={`${t("global.internal_number", "Internal number")}*`} />
        <TextField
          autoComplete="off"
          fullWidth
          id="internnumber"
          value={values.internnumber}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.internnumber && errors.internnumber ? true : false}
          helperText={touched.internnumber && errors.internnumber ? errors.internnumber : null}
          variant="outlined"
          InputProps={{
            disableUnderline: true
          }}
        />
      </Grid>
      <Grid xs={12} item>
        <SidebarInputLabel labelText={t("global.license_plate", "License plate")} />
        <TextField
          autoComplete="off"
          fullWidth
          id="regNumber"
          value={values.regNumber}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.regNumber && errors.regNumber ? true : false}
          helperText={touched.regNumber && errors.regNumber ? errors.regNumber : null}
          variant="outlined"
          InputProps={{
            disableUnderline: true
          }}
          disabled={editMode}
        />
      </Grid>
      <Grid xs={12} item>
        <SidebarInputLabel labelText={t("global.vehicle_type", "Vehicle type")} />
        <TextField
          autoComplete="off"
          fullWidth
          id="vehicleType"
          value={values.vehicleType}
          onChange={handleChange}
          onBlur={handleBlur}
          variant="outlined"
          InputProps={{
            disableUnderline: true
          }}
        />
      </Grid>
      <Grid xs={12} item>
        <SidebarInputLabel labelText={`${t("global.company", "Company")}*`} />
        <StyledMaxWidthAutocomplete
          id="companyId"
          options={companies}
          getOptionLabel={(option) => (option.name ? option.name : "")}
          value={companies.find((i) => i.id === values.companyId) || null}
          getOptionSelected={(option, value) => {
            return option.id === value.id
          }}
          onChange={(e, value) => {
            setFieldValue("companyId", value?.id)
            setFieldValue("departmentId", null)
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              name="companyId"
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.companyId && errors.companyId ? true : false}
              helperText={touched.companyId && errors.companyId ? errors.companyId : null}
            />
          )}
        />
      </Grid>
      <Grid xs={12} item>
        <SidebarInputLabel labelText={t("global.department", "Department")} />
        <StyledMaxWidthAutocomplete
          id="departmentId"
          options={departments.filter((dept) => dept.companyId === values.companyId)}
          getOptionLabel={(option) => (option.name ? option.name : "")}
          value={departments.find((i) => i.id === values.departmentId) || null}
          getOptionSelected={(option, value) => {
            return option.id === value.id
          }}
          onChange={(e, value) => {
            setFieldValue("departmentId", value?.id)
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              name="departmentId"
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.departmentId && errors.departmentId ? true : false}
              helperText={touched.departmentId && errors.departmentId ? errors.departmentId : null}
            />
          )}
        />
      </Grid>
      <Grid xs={12} item>
        <SidebarInputLabel labelText={`${t("global.meter_setting", "Meter setting")}*`} />
        <TextField
          autoComplete="off"
          InputProps={{
            disableUnderline: true,
            InputProps: { min: 0 }
          }}
          fullWidth
          id="meterSetting"
          value={values.meterSetting}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.meterSetting && errors.meterSetting ? true : false}
          helperText={touched.meterSetting && errors.meterSetting ? errors.meterSetting : null}
          variant="outlined"
          type="number"
        />
      </Grid>
      {showEnvironmentReportFields && (
        <>
          <Grid xs={12} item>
            <SidebarInputLabel labelText={t("global.fuel", "Fuel")} />
            {mainPropellantResult.status === 2 && (
              <StyledMaxWidthAutocomplete
                id="mainPropellantId-autocomplete"
                options={mainPropellantResult?.data}
                getOptionLabel={(option) => (option.name ? option.name : "")}
                value={mainPropellantResult.data.find((i) => i.id === values.mainPropellantId) || null}
                onChange={(e, value) => setFieldValue("mainPropellantId", value?.id || null)}
                onOpen={handleBlur}
                includeInputInList
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={touched.mainPropellantId && errors.mainPropellantId ? true : false}
                    helperText={
                      touched.mainPropellantId && errors.mainPropellantId ? errors.mainPropellantId : null
                    }
                    name="mainPropellantId"
                    variant="outlined"
                  />
                )}
              />
            )}
          </Grid>
          <Grid xs={12} item>
            <SidebarInputLabel
              labelText={t("vehicleregistry.average_fuel_consumption_km", "Average fuel consumption km")}
            />
            <TextField
              autoComplete="off"
              InputProps={{
                disableUnderline: true
              }}
              fullWidth
              id="basicFuelConsumptionKm"
              value={values.basicFuelConsumptionKm}
              onChange={handleChange}
              onBlur={handleBlur}
              variant="outlined"
              type="number"
            />
          </Grid>
          <Grid xs={12} item>
            <SidebarInputLabel labelText={t("vehicleregistry.average_speed_km_h", "Average speed km/h")} />
            <TextField
              autoComplete="off"
              InputProps={{
                disableUnderline: true
              }}
              fullWidth
              id="averageSpeed"
              value={values.averageSpeed}
              onChange={handleChange}
              onBlur={handleBlur}
              variant="outlined"
              type="number"
            />
          </Grid>
        </>
      )}
      <Grid xs={12} item>
        <SidebarInputLabel labelText={t("global.tachograph", "Tachograph")} />
        <FormControlLabel
          control={
            <Switch
              onChange={(item) => {
                const checked = item.target.checked
                setFieldValue("tachographIntegrationActive", checked)
              }}
              checked={values.tachographIntegrationActive}
            />
          }
        />
      </Grid>
      <Grid xs={12} item>
        <SidebarInputLabel labelText={t("global.disable_vehicle", "Disable vehicle")} />
        <FormControlLabel
          control={
            <Switch
              onChange={(item) => {
                const checked = item.target.checked
                setFieldValue("archived", checked)
              }}
              checked={values.archived}
            />
          }
        />
      </Grid>
    </Grid>
  )
}

export const VehicleForm = ({
  vehicleResult,
  onSubmit,
  editMode,
  addOrUpdateResultState,
  mainPropellantResult,
  mainPropellantApi

}) => {
  const { t } = useTranslation()
  const initialValues = editMode
    ? { ...vehicleResult.data }
    : {
        id: 0,
        companyId: null,
        departmentId: null,
        regNumber: "",
        internnumber: "",
        vehicleType: "",
        // meterSetting: null,
        meterSetting: 0,
        basicFuelConsumptionKm: 0,
        averageSpeed: 0,
        mainPropellantId: null,
        tachographIntegrationActive: false,
        archived: false,
      }
  const formRef = useRef()
  const [initialFormikValues, setInitialFormikValues] = useState(initialValues)
  const isValidEditMode =
    editMode &&
    vehicleResult.status === 2

  useEffect(() => {
    if (vehicleResult.status !== 2)
      return
    setInitialFormikValues(initialValues)
  }, [vehicleResult])


  const handleSubmit = (values) => {
    onSubmit(values)
  }

  return (
    <Sidebar
      initialFormikValues={initialFormikValues}
      setInitialFormikValues={setInitialFormikValues}
      formResult={addOrUpdateResultState}
      handleSubmit={handleSubmit}
      validationSchema={defaultSchema(t)}
      formData={{
        formId: "VehicleList",
        titleNew: t("global.add_vehicle", "Add vehicle"),
        subTitleNew: t("global.features.title.vehicleregistry", "Vehicle registry"),
        titleEdit: vehicleResult?.data?.regNumber,
        subTitleEdit: vehicleResult?.data?.internnumber,
        submitText: t("global.add_vehicle", "Add vehicle"),
        submitEditText: t("global.save_vehicle", "Save vehicle"),
        icon: faBusAlt
      }}
      formRef={formRef}>
      <>
        {isValidEditMode || !editMode ? (
          <VehicleFormComponent
            editMode={editMode}
            mainPropellantResult={mainPropellantResult}
            mainPropellantApi={mainPropellantApi}
          />
        ) : (
          <VehicleLoadingform />
        )}
      </>
    </Sidebar>
  )
}
